import { HeadingSection, VFlow } from 'bold-ui'
import {
  EnderecoFieldGroup,
  EnderecoFieldGroupModel,
  Form,
  FormProps,
  FormRenderProps,
  validate as validateEndereco,
} from 'components/form'
import { resolveValue } from 'components/form/final-form/useField'
import { DefaultFormFooter } from 'components/form/FormFooter'
import { FormPrompt } from 'components/form/FormPrompt'
import createCalculator from 'final-form-calculate'
import React, { useMemo } from 'react'
import { MetaPath, metaPath } from 'util/metaPath'
import { createValidator } from 'util/validation'
import {
  createDadosValidator,
  ProfissionalDadosFieldGroup,
} from 'view/profissional/components/ProfissionalDadosFieldGroup'

import ProfissionalFormModel from './ProfissionalFormModel'

export interface ProfissionalFormProps extends FormProps<ProfissionalFormModel> {
  edit?: boolean
  possuiLotacaoAtiva?: boolean
}

export const createProfissionalValidator = (possuiLotacaoAtiva: boolean) =>
  createValidator<ProfissionalFormModel>(
    {
      endereco: validateEndereco,
    },
    createDadosValidator(possuiLotacaoAtiva)
  )

export const createEnderecoCalculator = (meta: MetaPath<EnderecoFieldGroupModel>) =>
  createCalculator({
    field: meta.semNumero.absolutePath(),
    updates: {
      [meta.numero.absolutePath()]: (value: boolean, values: any) => {
        const endereco = resolveValue(values, meta)
        return value ? 'S/N' : endereco.numero === 'S/N' ? undefined : endereco.numero
      },
    },
  })

const path = metaPath<ProfissionalFormModel>()

const enderecoCalculator = createEnderecoCalculator(path.endereco)

export function ProfissionalDadosForm(props: ProfissionalFormProps) {
  const { edit, possuiLotacaoAtiva } = props

  const validate = useMemo(() => createProfissionalValidator(possuiLotacaoAtiva), [possuiLotacaoAtiva])

  const renderForm = (formProps: FormRenderProps<ProfissionalFormModel>) => {
    return (
      <form noValidate>
        <FormPrompt />
        <VFlow vSpacing={2}>
          <HeadingSection level={2} title='Dados pessoais'>
            <ProfissionalDadosFieldGroup
              name={path}
              formProps={formProps}
              edit={edit}
              possuiLotacaoAtiva={possuiLotacaoAtiva}
            />
          </HeadingSection>

          <HeadingSection level={2} title='Endereço residencial'>
            <EnderecoFieldGroup name={path.endereco} formProps={formProps} />
          </HeadingSection>

          <DefaultFormFooter handleSubmit={formProps.handleSubmit} />
        </VFlow>
      </form>
    )
  }

  return (
    <Form<ProfissionalFormModel>
      render={renderForm}
      validate={validate}
      decorators={[enderecoCalculator]}
      validateOnBlur={false}
      {...props}
    />
  )
}
